import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TimeSheetService } from './time-sheet.service';
import { IDiem, ITimeSheet, ITimeSheetData, ITransaction, ITrip, ITripsAndPerDiems } from './time-sheets.type';
import * as moment from 'moment';
import { IPayCategory } from '../pay-categories/pay-categories.type';
import { PayCategoriesService } from '../pay-categories/pay-categories.service';
import { IPilot } from '../pay-scales/pay-scales.types';
import { map } from 'rxjs/operators';
import * as _ from 'lodash';
import { HttpErrorResponse } from '@angular/common/http';
import { PilotsService } from '../../shared/services/pilots.service';

@Injectable()
export class TimeSheetsStoreService {
	get timeSheet(): ITimeSheetData {
		return this.timeSheetSubject.getValue();
	}

	set timeSheet(val: ITimeSheetData) {
		this.timeSheetSubject.next(val);
	}

	get timeSheets(): ITimeSheet[] {
		return this.timeSheetsSubject.getValue();
	}

	set timeSheets(val: ITimeSheet[]) {
		this.timeSheetsSubject.next(val);
	}

	constructor(
		private timeSheetService: TimeSheetService,
		private payCategoriesService: PayCategoriesService,
		private pilotsService: PilotsService
	) {
		this.timeSheet$ = this.timeSheetSubject.asObservable();
		this.timeSheets$ = this.timeSheetsSubject.asObservable();
	}

	public tripsAndPerDiems: ITripsAndPerDiems[] = [];
	public readonly timeSheets$: Observable<ITimeSheet[]>;
	public readonly timeSheet$: Observable<any>;

	private readonly timeSheetSubject: BehaviorSubject<ITimeSheetData> = new BehaviorSubject<ITimeSheetData>(
		this.createEmptyTimeSheetData()
	);
	private readonly timeSheetsSubject: BehaviorSubject<ITimeSheet[]> = new BehaviorSubject<ITimeSheet[]>(null);
	public addTransaction(payload: ITransaction): Observable<any> {
		return this.timeSheetService
			.addTransaction(payload)
			.pipe(map((ts: ITimeSheet) => this.refreshTimeSheetData(ts)));
	}

	public updateTransaction(payload: ITransaction): Observable<any> {
		return this.timeSheetService
			.updateTransaction(payload)
			.pipe(map((ts: ITimeSheet) => this.refreshTimeSheetData(ts)));
	}

	public deleteTransaction(timeSheetTransactionId: number): Observable<any> {
		return this.timeSheetService
			.deleteTransaction(timeSheetTransactionId)
			.pipe(map((ts: ITimeSheet) => this.refreshTimeSheetData(ts)));
	}

	public addPerDiem(payload: IDiem): Observable<any> {
		return this.timeSheetService.addPerDiem(payload).pipe(map((ts: ITimeSheet) => this.refreshTimeSheetData(ts)));
	}

	public updatePerDiem(payload: IDiem): Observable<any> {
		return this.timeSheetService
			.updatePerDiem(payload)
			.pipe(map((ts: ITimeSheet) => this.refreshTimeSheetData(ts)));
	}

	public deletePerDiem(timeSheetPerDiemId: number): Observable<any> {
		return this.timeSheetService
			.deletePerDiem(timeSheetPerDiemId)
			.pipe(map((ts: ITimeSheet) => this.refreshTimeSheetData(ts)));
	}

	public addTrip(payload: ITrip): Observable<any> {
		return this.timeSheetService.createTrip(payload).pipe(map((ts: ITimeSheet) => this.refreshTimeSheetData(ts)));
	}

	public approveTrip(tripId: number, tripStatus: boolean): Observable<any> {
		return this.timeSheetService
			.approveTrip(tripId, tripStatus)
			.pipe(map((ts: ITimeSheet) => this.refreshTimeSheetData(ts)));
	}

	public deleteTrip(timeSheetTripId: number): Observable<any> {
		return this.timeSheetService
			.deleteTrip(timeSheetTripId)
			.pipe(map((ts: ITimeSheet) => this.refreshTimeSheetData(ts)));
	}

	public updateTrip(timeSheetTripId: number, payload: ITrip): Observable<any> {
		return this.timeSheetService
			.updateTrip(timeSheetTripId, payload)
			.pipe(map((ts: ITimeSheet) => this.refreshTimeSheetData(ts)));
	}

	public setEmployeeTimeSheets(employeeId: number): void {
		this.timeSheetService.getTimeSheets(employeeId).subscribe(resp => (this.timeSheets = resp));
	}

	public getCurrentTimeSheetData(employeeId: number, date: string): void {
		this.loadTimeSheet(employeeId, date);
	}

	public submitTimeSheet(timeSheetStatus: number): Observable<void> {
		return this.timeSheetService
			.submitTimeSheet(this.timeSheet.timeSheet.timeSheetId, timeSheetStatus)
			.pipe(map((ts: ITimeSheet) => this.refreshTimeSheetData(ts)));
	}

	public getPreviousNotes(noteSettingsId: number): Observable<string[]> {
		return this.timeSheetService.getTimeSheetTransactionNoteValues(noteSettingsId);
	}

	private loadPayCategories(): Promise<IPayCategory[]> {
		return this.payCategoriesService.getPayCategories(false, true).toPromise();
	}

	private loadCurrentPilot(employeeId: number, date?: string): Promise<IPilot> {
		return this.pilotsService.getPilot(employeeId, true, date).toPromise();
	}

	private loadTimeSheet(employeeId: number, date: string): void {
		this.timeSheetService.getCurrentTimeSheet(employeeId, date).subscribe(
			(ts: ITimeSheet) => {
				const timeSheetData: ITimeSheetData = this.createEmptyTimeSheetData();
				this.clearTimeSheetData(timeSheetData);
				timeSheetData.state = {
					employeeId: employeeId,
					queryDate: date
				};
				timeSheetData.timeSheet = ts;
				this.setTimeSheetData(timeSheetData);
				this.setEmployeeTimeSheets(employeeId);
			},
			(error: HttpErrorResponse) => {
				const timeSheetData: ITimeSheetData = this.timeSheet;
				this.clearTimeSheetData(timeSheetData);
				timeSheetData.error = error.error;
				this.timeSheetSubject.next(timeSheetData);
			}
		);
	}

	private async setTimeSheetData(timeSheetData: ITimeSheetData): Promise<void> {
		if (!timeSheetData) {
			return;
		}

		if (
			timeSheetData.currentEmployee &&
			timeSheetData.timeSheet &&
			timeSheetData.currentEmployee.employeeId !== timeSheetData.timeSheet.employeeId
		) {
			// make sure employee and time sheets match. if not, reload employee
			timeSheetData.currentEmployee = null;
		}

		if (!timeSheetData.currentEmployee && timeSheetData.state) {
			timeSheetData.currentEmployee = await this.loadCurrentPilot(
				timeSheetData.state.employeeId,
				timeSheetData.timeSheet.startDate
			);
		}

		if (!timeSheetData.payCategories) {
			timeSheetData.payCategories = await this.loadPayCategories();
		}

		this.tripsAndPerDiems = [];

		if (
			timeSheetData.timeSheet &&
			timeSheetData.timeSheet.perDiems &&
			timeSheetData.timeSheet.perDiems.length > 0
		) {
			timeSheetData.trips.forEach(trip => (trip.isAssigned = false));
			this.groupTripsWithPerDiem(timeSheetData.timeSheet);
			this.sortTrips(timeSheetData.timeSheet);
		} else if (timeSheetData.timeSheet.trips && timeSheetData.timeSheet.trips.length > 0) {
			this.sortTripsWithNoPerDiem(timeSheetData.timeSheet);
		}

		if (timeSheetData.payCategories) {
			this.mapTransactionsPayCategory(timeSheetData.timeSheet, timeSheetData.payCategories);
		}

		const newTimeSheetData: ITimeSheetData = {
			payCategories: timeSheetData.payCategories,
			currentEmployee: timeSheetData.currentEmployee,
			perDiems: timeSheetData.timeSheet.perDiems,
			trips: timeSheetData.timeSheet.trips,
			transactions: timeSheetData.timeSheet.transactions,
			timeSheet: timeSheetData.timeSheet,
			tripsAndPerDiems: this.tripsAndPerDiems,
			state: timeSheetData.state
		};

		this.timeSheetSubject.next(newTimeSheetData);
	}

	private createEmptyTimeSheetData(): ITimeSheetData {
		return {
			payCategories: null,
			currentEmployee: null,
			perDiems: [],
			trips: [],
			transactions: [],
			timeSheet: null,
			tripsAndPerDiems: [],
			state: null,
			error: null
		};
	}

	private clearTimeSheetData(timeSheetData: ITimeSheetData): void {
		// reset calculated fields
		timeSheetData.perDiems = [];
		timeSheetData.transactions = [];
		timeSheetData.trips = [];
		timeSheetData.tripsAndPerDiems = [];
		timeSheetData.timeSheet = null;
	}

	private refreshTimeSheetData(timeSheet: ITimeSheet): void {
		const timeSheetData: ITimeSheetData = this.timeSheet;
		this.clearTimeSheetData(timeSheetData);

		// set the new time sheet data
		timeSheetData.timeSheet = timeSheet;
		this.setTimeSheetData(timeSheetData);
	}

	private mapTransactionsPayCategory(timeSheet: ITimeSheet, payCategories: IPayCategory[]): void {
		timeSheet.transactions.forEach(transaction => {
			const payCategory: IPayCategory = payCategories.find(
				item => item.payCategoryId.toString() === transaction.payCategoryId.toString()
			);
			if (payCategory) {
				transaction.payCategory = payCategory;
			}
		});
	}

	private groupTripsWithPerDiem(timeSheet: ITimeSheet): ITimeSheet {
		timeSheet.perDiems.forEach(p => {
			p.trips = [];

			timeSheet.trips.forEach(trip => {
				if (trip.isAssigned) {
					return;
				}

				//overlapping trip and per diem
				const isTripPerDiemOverlap: boolean =
					moment(p.perDiemStart).isSame(trip.tripEnd) ||
					moment(p.perDiemStart).isBetween(moment(trip.tripStart), moment(trip.tripEnd)) ||
					((moment(p.perDiemStart).isSame(trip.tripStart) &&
						moment(p.perDiemEnd).isBetween(moment(trip.tripStart), moment(trip.tripEnd))) ||
						moment(p.perDiemEnd).isBetween(moment(trip.tripStart), moment(trip.tripEnd))) ||
					moment(p.perDiemEnd).isSame(trip.tripStart);

				//trip is within the per diem
				const isTripWithinPerDiem: boolean =
					(moment(p.perDiemStart).isSame(trip.tripStart) &&
						moment(trip.tripEnd).isBetween(moment(p.perDiemStart), moment(p.perDiemEnd))) ||
					(moment(p.perDiemStart).isSame(trip.tripStart) && moment(p.perDiemEnd).isSame(trip.tripEnd)) ||
					(moment(p.perDiemEnd).isSame(trip.tripEnd) &&
						moment(trip.tripStart).isBetween(moment(p.perDiemStart), moment(p.perDiemEnd))) ||
					(moment(trip.tripStart) && moment(trip.tripEnd)).isBetween(
						moment(p.perDiemStart),
						moment(p.perDiemEnd)
					);

				if (isTripWithinPerDiem || isTripPerDiemOverlap) {
					p.trips.push(trip);
					trip.isAssigned = true;
				}
			});
			this.tripsAndPerDiems.push({
				date: p.perDiemStart,
				perDiem: p,
				trips: p.trips
			});
		});
		return timeSheet;
	}

	private sortTripsWithNoPerDiem(timeSheet: ITimeSheet): void {
		this.tripsAndPerDiems.push({
			date: timeSheet.trips[0].tripStart,
			perDiem: null,
			trips: timeSheet.trips
		});
	}

	private sortTrips(timeSheet: ITimeSheet): void {
		timeSheet.perDiems.forEach((perDiem, index) => {
			const nextItem: any = timeSheet.perDiems[index + 1];
			const before: any = [];
			const after: any = [];
			timeSheet.trips.forEach(trip => {
				if (moment(trip.tripStart).isBefore(perDiem.perDiemStart) && !trip.isAssigned) {
					before.push(trip);
					trip.isAssigned = true;
				} else if (!trip.isAssigned && ((nextItem && trip.tripStart < nextItem.perDiemStart) || !nextItem)) {
					after.push(trip);
					trip.isAssigned = true;
				}
			});
			if (before.length > 0) {
				this.tripsAndPerDiems.splice(index, 0, {
					date: before[0].tripStart,
					perDiem: null,
					trips: before
				});
			}
			if (after.length > 0) {
				this.tripsAndPerDiems.splice(index + 1, 0, {
					date: after[0].tripStart,
					perDiem: null,
					trips: after
				});
			}
		});
		this.tripsAndPerDiems = _.orderBy(
			this.tripsAndPerDiems,
			o => {
				return moment(o.date);
			},
			['asc']
		);
	}
}
